import {Trans} from '@lingui/macro';
import _, {noop, sortBy} from 'lodash';
import React, {FC} from 'react';
import styled from 'styled-components';

import {FrontClickEvent, Handler, HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {AsyncStatusesEnum} from '../../../../../libs/shared/helpers/src/types/asyncStatuses';
import {CheckBox} from '../../../../../libs/shared/ui/src/components/booleanInputs/checkBox';
import {Button} from '../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {ChromelessButton} from '../../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {Dropdown, DropdownProps} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {DropdownList} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownList';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import calendarMeetingRoom from '../../../../assets/images/calendarMeetingRoom.svg';
import {ChannelModel} from '../../../../models/api/channelModel';
import {MeetingRoomModel} from '../../../../models/api/meetingRoomModel';
import {DropdownCategory} from '../../../common/dropdowns/dropdownCategory';
import {DropdownEmpty} from '../../../common/dropdowns/dropdownEmpty';
import {AsyncLoader} from '../../../common/loaders/asyncLoader';
import {MeetingRoomDropdownItem} from './meetingRoomDropdownItem';

/*
 * Props.
 */

interface MeetingRoomsDropdownProps extends DropdownProps {
  meetingRooms: ReadonlyArray<MeetingRoomModel>;
  channel: ChannelModel;
  hasSearchQuery: boolean;
  status: AsyncStatusesEnum;
  hasInsufficientScopes: boolean;
  shouldIncludeUnavailableRooms: boolean;
  setShouldIncludeUnavailableRooms: HandlerOf<boolean>;
  onMeetingRoomClick: HandlerOf<MeetingRoomModel>;
  onRetryClick: Handler;
  onGoToReAuthChannelSettingsClick: Handler;
}

/*
 * Styles.
 */

const StyledPermissionsWarningDiv = styled.div`
  display: grid;
  padding: 0px 20px;
  justify-items: center;
  text-align: center;
  line-height: 24px;
`;

const StyledSettingsButton = styled(Button)`
  margin-top: 10px;
`;

const StyledAvailabilityDiv = styled.div`
  border-top: 1px solid ${(p) => p.theme.greys.shade30};
  height: 40px;
  display: flex;
  padding: 0px 14px;
  align-items: center;
`;

const StyledLoaderDiv = styled.div`
  padding: 20px;
`;

/*
 * Component.
 */

export const MeetingRoomsDropdown: FC<MeetingRoomsDropdownProps> = (props) => (
  <Dropdown {...props} isExclusive={false}>
    {renderContent(props)}
  </Dropdown>
);

function renderContent(props: MeetingRoomsDropdownProps) {
  const {shouldIncludeUnavailableRooms} = props;

  if (props.hasInsufficientScopes) {
    return (
      <DropdownEmpty imageSource={calendarMeetingRoom}>
        <StyledPermissionsWarningDiv onMouseDown={(e) => e.preventDefault()}>
          <Trans id="calendar-auth-prompt-meeting-rooms">
            Add calendar permissions to your {props.channel.displayName} inbox to see available meeting rooms.
          </Trans>
          <StyledSettingsButton
            variant={ButtonVariantsEnum.LINK}
            onClick={props.onGoToReAuthChannelSettingsClick}
          >
            <Trans id="calendar-auth-settings">Go to Settings</Trans>
          </StyledSettingsButton>
        </StyledPermissionsWarningDiv>
      </DropdownEmpty>
    );
  }

  const onClick = (e: FrontClickEvent) => {
    // Prevent Dropdown from closing.
    e.preventDefault();

    props.setShouldIncludeUnavailableRooms(!shouldIncludeUnavailableRooms);
  };

  return (
    <>
      {renderRoomsContent(props)}
      {props.status === AsyncStatusesEnum.LOADED && (
        <StyledAvailabilityDiv onMouseDown={(e) => e.preventDefault()}>
          <ChromelessButton onClick={onClick}>
            <CheckBox isChecked={shouldIncludeUnavailableRooms} onChange={noop}>
              <Trans id="meeting-rooms-include-unavailable">Include unavailable rooms</Trans>
            </CheckBox>
          </ChromelessButton>
        </StyledAvailabilityDiv>
      )}
    </>
  );
}

function renderRoomsContent(props: MeetingRoomsDropdownProps) {
  if (props.status === AsyncStatusesEnum.LOADED && props.meetingRooms.length === 0) {
    if (props.hasSearchQuery) {
      return <DropdownEmpty />;
    }

    return (
      <DropdownEmpty imageSource={calendarMeetingRoom}>
        <Trans id="meeting-rooms-none">No meeting rooms</Trans>
      </DropdownEmpty>
    );
  }

  if (props.status !== AsyncStatusesEnum.LOADED) {
    return (
      <StyledLoaderDiv onMouseDown={(e) => e.preventDefault()}>
        <AsyncLoader status={props.status} size={VisualSizesEnum.MEDIUM} onRetryClick={props.onRetryClick} />
      </StyledLoaderDiv>
    );
  }

  // Group rooms by their building name and display alphabetically.
  const roomGroups = _(props.meetingRooms)
    .groupBy((r) => r.buildingName)
    .sortBy((g) => g[0].buildingName && g[0].buildingName.toLowerCase())
    .value();

  return (
    <DropdownList {...props} shouldSelectFirst>
      {roomGroups.map((g) => renderMeetingRoomGroup(props, g))}
    </DropdownList>
  );
}

function renderMeetingRoomGroup(props: MeetingRoomsDropdownProps, rooms: ReadonlyArray<MeetingRoomModel>) {
  const buildingName = rooms.length > 0 && rooms[0].buildingName;
  const sortedRooms = sortBy(rooms, (r) => r.roomName.toLowerCase());

  return [
    buildingName && <DropdownCategory key={buildingName}>{buildingName}</DropdownCategory>,
    ...sortedRooms.map((r) => (
      <MeetingRoomDropdownItem key={r.address} meetingRoom={r} onClick={() => props.onMeetingRoomClick(r)} />
    )),
  ];
}
